import { Link } from "react-router-dom";
import { useCollapsePanel } from "hooks/useCollapsePanel";
import { v4 as uuidv4 } from "uuid";
import config from "config/config";
import {
  getBillingTypeCodeMapping,
  stringFormatingToPascal,
  stringFormattingToCurrency,
} from "js/formatting";
import { useTranslation, Trans } from "react-i18next";
import { useEffect, useState } from "react";
import { getFinancials } from "service/policy/getFinancials";
import {
  isAllowedToMakeCCPayment,
  getTypeOfPolicy,
  isFlagEnabled,
  isPolicyInactiveByPolicyStatus,
  isOldAlicUlPolicy,
  isPolicyInGrace,
} from "js/utilities";

import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import CardDisclaimerBase from "components/General/CardDisclaimerBase";
import Tooltip from "components/General/Tooltip";
import moment from "moment/moment";

export default function PaymentSummaryCard({ data }) {
  const uuid = uuidv4();
  const { t } = useTranslation();
  const [lapsedOn, setLapsedOn] = useState();
  const [isLoading, setIsLoading] = useState(false);
  useCollapsePanel(uuid);
  const policyType = getTypeOfPolicy(data?.product?.type);
  const isOldAlicUl = isOldAlicUlPolicy(data);
  const isEftAutopayEnrolled = data?.billingTypeCode === "02";
  const isAnnuityType =
    config.ENABLE_ANNUITIES === "true" && policyType === "annuity";
  const inGraceActiveNonOldUl =
    isPolicyInGrace(data) && !isOldAlicUl && lapsedOn;

  const hidePaymentStatusForProductType = ["LUE", "LU", "LZ"].includes(
    data?.product?.type
  );

  useEffect(() => {
    if (data?.contractID && !isAnnuityType) {
      setIsLoading(true);
      getFinancials(data)
        .then((response) => {
          const lapseDate = response.cashValues?.find(
            (cashValue) => moment(cashValue.projectedlapseDate) > moment()
          )?.projectedlapseDate;

          setLapsedOn(lapseDate);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("getFinancials failing", error);
          setIsLoading(false);
        });
    }
  }, [data]);

  return (
    <EverlakeLoadingOverlay isLoading={isLoading}>
      <div className="ui-card mb-4 md:mb-6">
        <div className="md:flex md:flex-row md:items-start">
          <div className="px-4 pt-3 md:flex-grow xl:px-6 xl:pt-6">
            <h2 className="mb-0">
              <button
                id={uuid}
                className="flex items-center group md:hidden"
                aria-controls={`${uuid}-panel`}
                aria-expanded="true"
                aria-label="Expand or collapse Payment Summary details"
              >
                <span className="inline-block p-1 rounded-full mr-2 text-ui-blue group-hover:text-white group-hover:bg-ui-blue">
                  <svg
                    className="ui-icon ui-plus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-plus"></use>
                  </svg>
                  <svg
                    className="ui-icon ui-minus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-minus"></use>
                  </svg>
                </span>
                <span className="text-base font-body font-bold md:ui-no-badge-icon-inline">
                  {t("CustomerPolicyDetails-payment-summary-title")}
                </span>
              </button>
              <span className="text-xl font-body font-bold hidden md:block">
                {t("CustomerPolicyDetails-payment-summary-title")}
              </span>
            </h2>
          </div>
          <hr className="mb-0 mt-3 md:hidden" />
          <div className="px-4 text-sm md:pt-4 md:justify-self-end xl:px-6 xl:pt-6">
            <Link to="/payment">
              {t("CustomerPolicyDetails-payment-summary-view-details")}
            </Link>
          </div>
        </div>
        <hr className="hidden md:block" />
        {!isAnnuityType && (
          <div
            id={`${uuid}-panel`}
            className="px-4 pb-4 overflow-hidden h-20 md:h-auto xl:px-6 xl:pb-6"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 md:mt-0 xl:gap-6 ">
              {!hidePaymentStatusForProductType && (
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("CustomerPolicyDetails-payment-summary-payment-status")}
                  </div>
                  <div className="flex text-sm text-theme-text-secondary font-semibold">
                    <p>{data?.paymentStatus}</p>
                    {data?.paymentStatus?.toLowerCase() === "returned" &&
                      isFlagEnabled(
                        "ENABLE_TOOLTIP_FOR_RETURNED_EFT_STATUS"
                      ) && (
                        <Tooltip
                          xMargin="mx-0"
                          content={
                            <Trans
                              i18nKey="CustomerPolicyDetails-payment-summary-alerts-correspondence"
                              components={{
                                link1: (
                                  <Link
                                    className="underline text-theme-action"
                                    to="/messages"
                                  ></Link>
                                ),
                              }}
                            />
                          }
                        />
                      )}
                  </div>
                </div>
              )}
              {isOldAlicUl && !isEftAutopayEnrolled ? null : (
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {isOldAlicUl && isEftAutopayEnrolled
                      ? t(
                          "CustomerPolicyDetails-payment-summary-planned-premium-date"
                        )
                      : t(
                          "CustomerPolicyDetails-payment-summary-payment-amount"
                        )}
                  </div>
                  <div className="text-sm text-theme-text-secondary font-semibold">
                    {isOldAlicUl && isEftAutopayEnrolled
                      ? data?.premiumDueDate
                      : stringFormattingToCurrency(
                          data?.modalPremiumAmountTotal
                        )}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {isOldAlicUl
                    ? t(
                        "CustomerPolicyDetails-payment-summary-planned-payment-frequency"
                      )
                    : t(
                        "CustomerPolicyDetails-payment-summary-last-payment-date"
                      )}
                </div>
                <div className="text-sm text-theme-text-secondary font-semibold">
                  {isOldAlicUl
                    ? stringFormatingToPascal(data?.billingMode)
                    : data?.lastPaymentDate}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {isOldAlicUl
                    ? t(
                        "CustomerPolicyDetails-payment-summary-planned-premium-amount"
                      )
                    : t(
                        "CustomerPolicyDetails-payment-summary-payment-due-date"
                      )}
                </div>
                <div className="text-sm text-theme-text-secondary font-semibold">
                  {isOldAlicUl
                    ? stringFormattingToCurrency(data?.modalPremiumAmountTotal)
                    : data?.premiumDueDate}
                </div>
              </div>
              {!isOldAlicUl && (
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t(
                      "CustomerPolicyDetails-payment-summary-payment-frequency"
                    )}
                  </div>
                  <div className="text-sm text-theme-text-secondary font-semibold">
                    {stringFormatingToPascal(data?.billingMode)}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("CustomerPolicyDetails-payment-summary-payment-Method")}
                </div>
                <div className="text-sm text-theme-text-secondary font-semibold">
                  {getBillingTypeCodeMapping(data?.billingTypeCode)}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("CustomerPolicyDetails-payment-summary-autopay")}
                </div>
                <div className="text-sm text-theme-text-secondary font-semibold">
                  {data?.billingTypeCode === "02" ||
                  data?.billingTypeCode === "06"
                    ? t("CustomerPolicyDetails-payment-summary-enrolled")
                    : t("CustomerPolicyDetails-payment-summary-not-enrolled")}
                </div>
              </div>
              {inGraceActiveNonOldUl && (
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("CustomerPolicyDetails-payment-summary-lapse-date")}
                  </div>
                  <div className="text-sm text-theme-text-secondary font-semibold">
                    {moment(lapsedOn).format("MM/DD/YYYY")}
                  </div>
                </div>
              )}
              {isOldAlicUl && (
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t(
                      "CustomerPolicyDetails-payment-summary-last-payment-date"
                    )}
                  </div>
                  <div className="text-sm text-theme-text-secondary font-semibold">
                    {data?.lastPaymentDate}
                  </div>
                </div>
              )}
            </div>

            <div className="text-right mt-6">
              {isAllowedToMakeCCPayment(data, "onetimepayment") &&
                (isPolicyInactiveByPolicyStatus(data?.status) ? (
                  <div className="flex items-center justify-end">
                    <button className="ui-btn-primary" disabled>
                      {t(
                        "CustomerPolicyDetails-payment-summary-make-a-payment"
                      )}
                    </button>
                    <Tooltip
                      content="Online payments are not available with this status"
                      xMargin="ml-1"
                    />
                  </div>
                ) : (
                  <Link to="/payment/make-a-payment">
                    <button className="ui-btn-primary">
                      {t(
                        "CustomerPolicyDetails-payment-summary-make-a-payment"
                      )}
                    </button>
                  </Link>
                ))}
            </div>
          </div>
        )}
        {(!isAllowedToMakeCCPayment(data, "onetimepayment") ||
          isAnnuityType) && (
          <CardDisclaimerBase
            disclaimer={t("CustomerPolicyDetails-payment-summary-disclaimer")}
          />
        )}
        {isOldAlicUl && (
          <CardDisclaimerBase
            disclaimer={t(
              "CustomerPolicyDetails-oldAlicUl-payment-summary-disclaimer"
            )}
          />
        )}
      </div>
    </EverlakeLoadingOverlay>
  );
}
